import { createContext, useState, useEffect, useContext } from "react";
import { getCurrentUser } from "../api/user.api"; // Adjust path as needed

export const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [currentView, setCurrentView] = useState(() => {
    return localStorage.getItem("currentView") || "default";
  });

  const [profileDetails, setProfileDetails] = useState(null);
  const [loading, setLoading] = useState(!user); // Only load if user doesn't exist

  // Fetch user data once when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setLoading(true);
        try {
          const response = await getCurrentUser();
          const userData = response?.data?.user;
          setUser(userData);
          setProfileDetails(userData);
          localStorage.setItem("user", JSON.stringify(userData));
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      } else {
        // If user exists in context, use it for profile details
        setProfileDetails(user);
      }
    };

    fetchUserData();
  }, [user]); // Only run if user doesn't exist

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
    localStorage.setItem("currentView", currentView);
  }, [user, currentView]);

  const switchView = (viewType) => {
    setCurrentView(viewType);
  };

  const canSwitchToOwner = user?.role === "client" || user?.role === "partner";
  const canSwitchToCompany =
    user?.role === "partner" || user?.role === "client";

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        profileDetails,
        currentView,
        switchView,
        canSwitchToOwner,
        canSwitchToCompany,
        loading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
